import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { CoverageDto, DecompteDto } from '@mnemo/shared';
import type { Locale, TranslationKey } from '../i18n.js';
import { ApiError, api, type SessionResponse } from '../lib/api.js';
import { EmptyState, ErrorState, Loading } from '../components/ui.js';
import { ActiveFilters } from '../components/table-bits.js';
import { DataStartNotice, FilterBar, useJobFilters } from '../components/JobFilters.js';
import { TotalsStrip } from '../components/TotalsStrip.js';

type T = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * Le décompte par personne (DEC-01..03) — demandé le 13.10.2026 pour facturer les copies
 * privées : « l'idéal serait d'avoir le total des copies pour chaque utilisateur ».
 *
 * Mêmes filtres que les Journaux, lus dans la même URL : on pose une période, le type
 * « Impressions + copies » et le centre de coût des copies privées, on passe d'un onglet à
 * l'autre, et la sélection suit. Une ligne par personne, de la plus chère à la moins chère,
 * avec le montant que uniFLOW a calculé. Un clic sur un nom ouvre ses travaux.
 *
 * Ce n'est pas la Synthèse, à dessein : la Synthèse ne nomme personne ; ici, la personne
 * est le sujet. Rien n'y est visible qui ne le soit déjà dans les Journaux, dans le même
 * périmètre, et l'export est journalisé comme les autres.
 */
export function Decompte({
  t,
  locale,
  session,
}: {
  t: T;
  locale: Locale;
  session: SessionResponse;
}) {
  const [params, setParams] = useSearchParams();
  const filters = useJobFilters(params, setParams, t, locale);
  const [data, setData] = useState<DecompteDto | null>(null);
  const [coverage, setCoverage] = useState<CoverageDto | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [errorCode, setErrorCode] = useState('INTERNAL');

  const tag = locale === 'de' ? 'de-CH' : 'fr-CH';
  const numbers = useMemo(() => new Intl.NumberFormat(tag), [tag]);
  const money = useMemo(
    () => new Intl.NumberFormat(tag, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    [tag],
  );

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      setData(await api.byPerson(filters.query));
      setStatus('ready');
    } catch (err) {
      setErrorCode(err instanceof ApiError ? err.code : 'INTERNAL');
      setStatus('error');
    }
    // `session.user?.id` à dessein, comme dans les Journaux : changer d'identité recharge.
  }, [filters.query, session.user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api
      .coverage()
      .then(setCoverage)
      .catch(() => undefined);
  }, [session.user?.id]);

  /** Les travaux d'une personne, dans les Journaux, avec la même sélection. */
  const jobsOf = (userName: string) => {
    const next = new URLSearchParams(params);
    next.set('user', userName);
    return { pathname: '/', search: `?${next.toString()}` };
  };

  const top = data?.items[0]?.price ?? 0;
  const scopeEmpty = session.user?.establishments?.length === 0;

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-4 p-4">
      <FilterBar
        t={t}
        locale={locale}
        filters={filters}
        userId={session.user?.id}
        exportHref={api.byPersonExportUrl(filters.query)}
        exportTitle={t('decompte.export')}
      />

      <DataStartNotice t={t} day={filters.day} from={filters.dates.from} coverage={coverage} />

      <ActiveFilters
        t={t}
        filters={filters.active}
        onRemove={filters.remove}
        onClear={filters.clear}
      />

      <section className="card overflow-hidden">
        {data && status !== 'error' && (
          <TotalsStrip
            t={t}
            locale={locale}
            lead={t('decompte.lead', {
              people: numbers.format(data.totals.people),
              jobs: numbers.format(data.totals.jobs),
            })}
            totals={data.totals}
          />
        )}
        {status === 'error' && <ErrorState t={t} code={errorCode} onRetry={() => void load()} />}
        {status === 'loading' && !data && <Loading t={t} />}
        {status === 'ready' && data?.items.length === 0 && (
          <EmptyState message={scopeEmpty ? t('state.emptyScope') : t('decompte.empty')} />
        )}

        {data && data.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-bg text-left text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th scope="col" className="px-3 py-2">
                    {t('decompte.col.person')}
                  </th>
                  <th scope="col" className="hidden px-3 py-2 text-right sm:table-cell">
                    {t('decompte.col.jobs')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    {t('decompte.col.bw')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    {t('decompte.col.color')}
                  </th>
                  <th scope="col" className="hidden px-3 py-2 text-right md:table-cell">
                    {t('decompte.col.pages')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    {t('decompte.col.amount')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p) => (
                  <tr
                    key={`${p.userId ?? ''}|${p.userName ?? ''}`}
                    className="border-t border-border"
                  >
                    <td className="px-3 py-2">
                      {p.userName ? (
                        <Link
                          to={jobsOf(p.userName)}
                          title={t('decompte.openJobs')}
                          className="underline-offset-2 hover:text-accent hover:underline"
                        >
                          {p.userName}
                        </Link>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="hidden px-3 py-2 text-right tabular-nums text-muted sm:table-cell">
                      {numbers.format(p.jobs)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {numbers.format(p.pagesBw)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {numbers.format(p.pagesColor)}
                    </td>
                    <td className="hidden px-3 py-2 text-right tabular-nums md:table-cell">
                      {numbers.format(p.pages)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="inline-flex items-center justify-end gap-3">
                        {/* La part de la personne dans le plus gros montant — décorative : le
                            montant est écrit à côté (Charte §7). */}
                        <span
                          aria-hidden="true"
                          className="hidden h-1.5 w-20 overflow-hidden rounded-full bg-bg sm:block"
                        >
                          <span
                            className="block h-full rounded-full bg-accent/70"
                            style={{
                              width: `${top > 0 ? Math.max(2, (p.price / top) * 100) : 0}%`,
                            }}
                          />
                        </span>
                        <span className="whitespace-nowrap font-semibold tabular-nums">
                          {money.format(p.price)}
                        </span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border font-semibold">
                  <td className="px-3 py-2">{t('decompte.total')}</td>
                  <td className="hidden px-3 py-2 text-right tabular-nums sm:table-cell">
                    {numbers.format(data.totals.jobs)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {numbers.format(data.totals.pagesBw)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {numbers.format(data.totals.pagesColor)}
                  </td>
                  <td className="hidden px-3 py-2 text-right tabular-nums md:table-cell">
                    {numbers.format(data.totals.pages)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-accent">
                    {money.format(data.totals.price)} CHF
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        {data?.truncated && (
          <p className="border-t border-border px-3 py-2 text-xs text-muted">
            {t('decompte.truncated', { shown: numbers.format(data.items.length) })}
          </p>
        )}
      </section>

      <p className="px-1 text-xs text-muted">{t('decompte.note')}</p>
    </div>
  );
}
